import el from "@cypherpotato/el";
import { joinObjects, roundDecimals } from './../util';
import { Badge } from "./badge";
import { Icon } from "./icon";
import { Section } from "./layout";
import { Button } from "./button";
import { showFlyout } from "../ui/flyout";
import { MutedText } from "./text";

export function FormControl(label, ...children) {
    return el(".form-control",
        label && el("label", label),
        ...children
    );
}

export function MultiSelect({ value, items, onChange, ...props }) {

    const selectedValues = el.state.create(value || []);
    return el(".app-input.multi-select[form-slot]",
        el.state.listening([selectedValues], () =>
            el("span", selectedValues.value.nullWhenEmpty()?.slice(0, 3).map(i => items.find(item => item.value === i)?.label || i).join(", ") || MutedText("Select options"))
        ),
        {
            onClick() {
                showFlyout(
                    el(".multi-select-options",
                        items.map(i => el("label.app-button.check-button",
                            i.label || i.text || i.value,
                            {
                                mount() {
                                    if (selectedValues.value.includes(i.value)) {
                                        this.classList.add("checked");
                                    }
                                },
                                onClick(event) {
                                    if (selectedValues.value.includes(i.value)) {
                                        selectedValues.value = selectedValues.value.filter(v => v !== i.value);
                                        this.classList.remove("checked");
                                    } else {
                                        selectedValues.value.push(i.value);
                                        this.classList.add("checked");
                                    }
                                    selectedValues.requestUpdate();
                                    onChange?.(selectedValues.value);
                                }
                            }
                        ))
                    )
                )
            },
            $properties: {
                value: {
                    get() {
                        return selectedValues.value;
                    },
                    set(value) {
                        selectedValues.value = value;
                    }
                }
            },
            ...props
        },
    )
}

export function Select({ value, items, itemsRaw = null, ...props }) {
    return el("select.app-input[form-slot]",
        {
            ...props
        },
        items?.map(i => {
            const groupItems = i.items || i.options;
            if (groupItems) {
                return el("optgroup",
                    { label: i.label || i.text },
                    groupItems.map(child => el("option", { value: child.value, selected: child.value == value }, child.text || child.label || child.value))
                );
            }

            return el("option", { value: i.value, selected: i.value == value }, i.text || i.label || i.value);
        }),
        itemsRaw
    )
}

export function TextInput(name, placeholder, ...args) {
    return el("input.app-input[form-slot][type=text]", {
        name,
        id: name,
        placeholder
    }, ...args);
}

export function TextArea(name, value, ...args) {
    return el("textarea.app-input[form-slot]", value, {
        name,
        id: name
    }, ...args);
}

export function Checkbox(name, label, ...args) {
    const checkboxId = Math.random();
    const input = el("input[form-slot][type=checkbox]", {
        name,
        id: checkboxId,
        style: {
            display: joinObjects(...args).hideCheckMark ? "none" : "block"
        }
    }, ...args);

    return el(".checkbox-wrapper",
        input,
        label && el("label", { for: checkboxId }, label),
        {
            $properties: {
                value: {
                    get() {
                        return input.checked;
                    },
                    set(value) {
                        input.checked = Boolean(value);
                    }
                }
            }
        }
    );
}

export function TagsInput(name, ...args) {
    const argsObj = joinObjects(...args);
    const tags = el.state.create(argsObj?.value || []);
    const onChange = argsObj?.onChange || null;

    tags.subscribe(value => {
        if (onChange) {
            onChange(value);
        }
    });

    return el("div.tags-input[form-slot=1]",
        Section(
            TextInput(null, "Separate values with commas", {
                onKeyDown(event) {
                    if (event.key === "Enter") {
                        event.preventDefault();
                        event.stopPropagation();

                        const value = event.target.value;
                        if (value.length > 0) {
                            tags.value.push(value);
                            tags.requestUpdate();
                            event.target.value = "";
                        }
                    }
                }
            })
        ),
        el.state.listening([tags], () =>
            Section(
                tags.value?.length && el(".tags.badge-list",
                    tags.value.map(tag =>
                        Badge(tag, null,
                            Icon("close-line", {
                                onClick() {
                                    tags.value = tags.value.filter(t => t !== tag);
                                    tags.requestUpdate();
                                }
                            })
                        )
                    )
                ) || null
            ),
        ),
        {
            id: name,
            name,
            $properties: {
                value: {
                    get() {
                        return tags.value;
                    },
                    set(value) {
                        tags.value = value;
                    }
                },
                length: {
                    get() {
                        return tags.value.length;
                    }
                }
            }
        }
    );
}

export function NumberInput({ value, step = 1, min = 0, max = 100, onChange = null, ariaLabel }) {

    const decimalCount = step.countDecimals();
    const number = el.state.create(value || min);

    number.subscribe(value => {
        if (onChange) {
            onChange(value);
        }
    });

    function valueParsed() {
        var v = roundDecimals(Math.min(Math.max(number.value, min), max), decimalCount);
        if (v == number.value) {
            return v;
        }
        number.value = v;
        return v;
    }

    return el.state.listening([number], () =>
        el(".number-input-wrapper",
            {
                $properties: {
                    value: {
                        get() {
                            return number.value;
                        },
                        set(value) {
                            number.value = value;
                        }
                    }
                }
            },
            Icon("subtract-line", {
                onClick() {
                    if (number.value > min) {
                        number.value = number.value - step;
                    }
                }
            }),
            el("input.app-input[type=number]", {
                value: valueParsed(),
                ariaLabel,
                onBlur(event) {
                    number.value = decimalCount == 0 ? parseInt(event.target.value) : parseFloat(event.target.value.replace(",", "."));
                }
            }),
            Icon("add-line", {
                onClick() {
                    if (number.value < max) {
                        number.value = number.value + step;
                    }
                }
            })
        )
    );
}
